import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';

import { USER_ROLE } from '@/shared/constants';

export interface UserInfo {
  userId: number;
  instructorId: number;
  academyUserId: number;
  studentId: number;
  loginId: string;
  userName: string;
  phone: string;
  userRole: (typeof USER_ROLE)[keyof typeof USER_ROLE] | '';
  userType: string;
  academy: {
    academyId: number;
    academyPublicId: string;
    academyName: string;
    academyStatus: string;
  };
}

export interface AuthState {
  userInfo: UserInfo;
  pendingNavigation: string | null;
  setUserInfo: (userInfo: AuthState['userInfo']) => void;
  updateUserInfo: (partial: Partial<AuthState['userInfo']>) => void;
  setPendingNavigation: (path: string | null) => void;
  resetUserInfo: () => void;
}

const initialUserInfo: AuthState['userInfo'] = {
  userId: 0,
  instructorId: 0,
  academyUserId: 0,
  studentId: 0,
  loginId: '',
  userName: '',
  phone: '',
  userType: '',
  userRole: '',
  academy: {
    academyId: 0,
    academyPublicId: '',
    academyName: '',
    academyStatus: '',
  },
};

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set) => ({
        userInfo: initialUserInfo,
        pendingNavigation: null,
        setUserInfo: (userInfo) => set({ userInfo }),
        updateUserInfo: (partial) => set((state) => ({ userInfo: { ...state.userInfo, ...partial } })),
        setPendingNavigation: (path) => set({ pendingNavigation: path }),
        resetUserInfo: () => set({ userInfo: initialUserInfo, pendingNavigation: null }),
      }),
      {
        name: 'AuthStore',
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({ userInfo: state.userInfo }),
      }
    ),
    {
      name: 'AuthStore',
      enabled: import.meta.env.VITE_ENVIRONMENT === 'development' || import.meta.env.VITE_ENVIRONMENT === 'local',
    }
  )
);
